import React, { useMemo } from 'react';
import {
  ConsumoPorUsuario,
  fetchOperacoesDeConsumo,
  OperacaoDeConsumo,
  ResumoDeConsumo,
  TotaisDeConsumo,
} from '../../../../api/consumoIaApi';
import TabelaSistema, { ColunaTabela } from '../../../../components/sync-painel/TabelaSistema';
import ListaPaginada from './ListaPaginada';
import '../../../../shared/feedback/aviso-de-tela.css';
import {
  fmtDia,
  fmtInt,
  fmtMes,
  fmtQuando,
  fmtUsd,
  nomeDoUsuario,
  rotuloDeOrigem,
  SEM_USUARIO_EXPLICACAO,
} from './formatacao';

/**
 * Aba "Radar e Assistente": o consumo gravado pelo próprio SOAC — o assistente do Dashboard
 * Oficina e os Radares (redação dos cartões e achados do assistente).
 *
 * Toda tabela é a `TabelaSistema`, o padrão das telas do sistema (pedido do Victor, 20/10/2026).
 */

export const CartaoDeTotais: React.FC<{ titulo: string; custo: number; detalhe: string }> = ({
  titulo,
  custo,
  detalhe,
}) => (
  <div className="admin-card" style={{ flex: 1, minWidth: 220 }}>
    <div className="admin-card-title">{titulo}</div>
    <div style={{ fontSize: 28, fontWeight: 700, margin: '4px 0 8px' }}>{fmtUsd(custo)}</div>
    <div className="admin-texto-secundario">{detalhe}</div>
  </div>
);

/** Card com título e uma tabela no padrão do sistema. */
export function CardDeTabela<T>({
  titulo,
  colunas,
  itens,
  chave,
  vazio,
}: {
  titulo: string;
  colunas: ColunaTabela<T>[];
  itens: T[];
  chave: (item: T) => string;
  vazio: string;
}) {
  return (
    <div className="admin-card" style={{ marginTop: 16 }}>
      <div className="admin-card-title">{titulo}</div>
      <TabelaSistema colunas={colunas} itens={itens} chave={chave} vazio={vazio} />
    </div>
  );
}

function detalheDosTotais(t: TotaisDeConsumo): string {
  return (
    `${fmtInt(t.operacoes)} ${t.operacoes === 1 ? 'operação' : 'operações'} · ` +
    `${fmtInt(t.tokensEntrada + t.cacheLido + t.cacheEscrito)} tokens de entrada · ` +
    `${fmtInt(t.tokensSaida)} de saída`
  );
}

/** As colunas de totais, depois das colunas que identificam a linha. */
function colunasDeTotais<T extends TotaisDeConsumo>(): ColunaTabela<T>[] {
  return [
    { titulo: 'Operações', largura: 90, texto: (t) => fmtInt(t.operacoes) },
    { titulo: 'Idas ao modelo', largura: 110, texto: (t) => fmtInt(t.idasAoModelo) },
    { titulo: 'Tokens de entrada', largura: 130, texto: (t) => fmtInt(t.tokensEntrada) },
    { titulo: 'Tokens de saída', largura: 120, texto: (t) => fmtInt(t.tokensSaida) },
    { titulo: 'Cache lido', largura: 100, texto: (t) => fmtInt(t.cacheLido) },
    { titulo: 'Cache escrito', largura: 100, texto: (t) => fmtInt(t.cacheEscrito) },
    { titulo: 'Custo estimado', largura: 120, texto: (t) => fmtUsd(t.custoEstimadoUsd) },
  ];
}

const COLUNAS_DAS_OPERACOES: ColunaTabela<OperacaoDeConsumo>[] = [
  { titulo: 'Quando', largura: 140, texto: (o) => fmtQuando(o.ocorridoEm) },
  {
    titulo: 'Quem',
    largura: 160,
    larguraMaxima: 220,
    texto: (o) => nomeDoUsuario(o.usuarioId, o.usuarioNome),
    render: (o) => (
      <span title={o.usuarioId ? undefined : SEM_USUARIO_EXPLICACAO}>
        {nomeDoUsuario(o.usuarioId, o.usuarioNome)}
      </span>
    ),
  },
  { titulo: 'Para quê', largura: 260, larguraMaxima: 340, texto: (o) => rotuloDeOrigem(o.origem) },
  { titulo: 'Modelo', largura: 140, texto: (o) => o.modelo },
  { titulo: 'Idas ao modelo', largura: 110, texto: (o) => fmtInt(o.idasAoModelo) },
  {
    titulo: 'Tokens (entrada / saída)',
    largura: 170,
    texto: (o) => `${fmtInt(o.tokensEntrada + o.cacheLido + o.cacheEscrito)} / ${fmtInt(o.tokensSaida)}`,
  },
  {
    titulo: 'Custo estimado',
    largura: 120,
    texto: (o) => (o.custoEstimadoUsd == null ? 'Sem preço na tabela' : fmtUsd(o.custoEstimadoUsd)),
  },
];

const COLUNAS_POR_USUARIO: ColunaTabela<ConsumoPorUsuario>[] = [
  {
    titulo: 'Usuário',
    largura: 180,
    larguraMaxima: 240,
    texto: (u) => nomeDoUsuario(u.usuarioId, u.nome),
    render: (u) => (
      <span title={u.usuarioId ? undefined : SEM_USUARIO_EXPLICACAO}>{nomeDoUsuario(u.usuarioId, u.nome)}</span>
    ),
  },
  ...colunasDeTotais<ConsumoPorUsuario>(),
];

/** Linha da tabela por mês: o total do mês em destaque, seguido de cada origem. */
type LinhaDoMes = TotaisDeConsumo & { chave: string; mes: string | null; origem: string | null };

const COLUNAS_POR_MES: ColunaTabela<LinhaDoMes>[] = [
  {
    titulo: 'Mês',
    largura: 150,
    texto: (l) => (l.mes ? fmtMes(l.mes) : ''),
    render: (l) => (l.mes ? <strong>{fmtMes(l.mes)}</strong> : null),
  },
  {
    titulo: 'Origem',
    largura: 260,
    larguraMaxima: 340,
    texto: (l) => (l.origem ? rotuloDeOrigem(l.origem) : 'Total do mês'),
    render: (l) => (l.origem ? rotuloDeOrigem(l.origem) : <strong>Total do mês</strong>),
  },
  ...colunasDeTotais<LinhaDoMes>(),
];

type ComOrigem = TotaisDeConsumo & { origem: string };
type ComModelo = TotaisDeConsumo & { modelo: string };
type ComDia = TotaisDeConsumo & { dia: string };

const COLUNAS_POR_ORIGEM: ColunaTabela<ComOrigem>[] = [
  { titulo: 'Origem', largura: 260, larguraMaxima: 340, texto: (o) => rotuloDeOrigem(o.origem) },
  ...colunasDeTotais<ComOrigem>(),
];
const COLUNAS_POR_MODELO: ColunaTabela<ComModelo>[] = [
  { titulo: 'Modelo', largura: 180, texto: (m) => m.modelo },
  ...colunasDeTotais<ComModelo>(),
];
const COLUNAS_POR_DIA: ColunaTabela<ComDia>[] = [
  { titulo: 'Dia', largura: 110, texto: (d) => fmtDia(d.dia) },
  ...colunasDeTotais<ComDia>(),
];

const buscarOperacoes = (pagina: number) => fetchOperacoesDeConsumo(pagina);

const AbaRadarAssistente: React.FC<{ resumo: ResumoDeConsumo }> = ({ resumo }) => {
  const semConsumo = resumo.ultimos30Dias.operacoes === 0 && resumo.mesCorrente.operacoes === 0;

  const linhasDoMes = useMemo<LinhaDoMes[]>(
    () =>
      resumo.porMesEOrigem.flatMap((m) => [
        { ...m.total, chave: `${m.mes}-total`, mes: m.mes, origem: null },
        ...m.origens.map((o) => ({ ...o, chave: `${m.mes}-${o.origem}`, mes: null, origem: o.origem })),
      ]),
    [resumo.porMesEOrigem],
  );

  return (
    <>
      {resumo.modelosSemPreco.length > 0 && (
        <p className="aviso-de-tela">
          {resumo.modelosSemPreco.length === 1 ? 'O modelo' : 'Os modelos'}{' '}
          {resumo.modelosSemPreco.join(', ')} não {resumo.modelosSemPreco.length === 1 ? 'está' : 'estão'}{' '}
          na tabela local de preços: os tokens aparecem nas contagens, mas ficam fora da soma de
          custo. Acione a TI para atualizar a tabela.
        </p>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
        <CartaoDeTotais titulo="Hoje" custo={resumo.hoje.custoEstimadoUsd} detalhe={detalheDosTotais(resumo.hoje)} />
        <CartaoDeTotais titulo="Mês corrente" custo={resumo.mesCorrente.custoEstimadoUsd} detalhe={detalheDosTotais(resumo.mesCorrente)} />
        <CartaoDeTotais
          titulo={`Últimos ${resumo.janelaDias} dias`}
          custo={resumo.ultimos30Dias.custoEstimadoUsd}
          detalhe={detalheDosTotais(resumo.ultimos30Dias)}
        />
      </div>

      {semConsumo ? (
        <p className="admin-vazio" style={{ marginTop: 16 }}>
          Nenhum consumo registrado até agora. O registro começa na primeira pergunta ao assistente
          ou na primeira análise de um Radar.
        </p>
      ) : (
        <>
          {resumo.porUsuario && (
            <CardDeTabela
              titulo={`Por usuário (últimos ${resumo.janelaDias} dias)`}
              colunas={COLUNAS_POR_USUARIO}
              itens={resumo.porUsuario}
              chave={(u) => u.usuarioId ?? 'sem-usuario'}
              vazio="Nenhum uso nos últimos 30 dias."
            />
          )}

          <ListaPaginada
            titulo="Cada operação"
            dica={
              <>
                Uma linha por uso da IA: quem pediu, para quê e quando. <strong>Sistema (sem
                usuário)</strong> é o que roda sozinho (a rotina diária do Radar e a redação dos
                cartões) e todo registro anterior a 20/10/2026, quando o sistema passou a guardar
                quem pediu.
              </>
            }
            buscar={buscarOperacoes}
            colunas={COLUNAS_DAS_OPERACOES}
            chave={(o) => o.id}
            vazio="Nenhuma operação registrada."
            erro="Não foi possível carregar a lista de operações. Recarregue a página; se persistir, acione a TI."
          />

          {/*
            Por MÊS e origem (contac/at-13): "quanto foi o mês, e quanto veio de cada ponto". A
            quebra por origem abaixo continua útil, mas a janela dela são 30 dias corridos, que
            atravessam a virada do mês.
          */}
          <CardDeTabela
            titulo={`Por mês e origem (últimos ${resumo.mesesDaQuebra} meses)`}
            colunas={COLUNAS_POR_MES}
            itens={linhasDoMes}
            chave={(l) => l.chave}
            vazio="Nenhum consumo nos últimos meses."
          />
          <CardDeTabela
            titulo={`Por origem (últimos ${resumo.janelaDias} dias)`}
            colunas={COLUNAS_POR_ORIGEM}
            itens={resumo.porOrigem}
            chave={(o) => o.origem}
            vazio="Nenhum consumo nos últimos 30 dias."
          />
          <CardDeTabela
            titulo={`Por modelo (últimos ${resumo.janelaDias} dias)`}
            colunas={COLUNAS_POR_MODELO}
            itens={resumo.porModelo}
            chave={(m) => m.modelo}
            vazio="Nenhum consumo nos últimos 30 dias."
          />
          <CardDeTabela
            titulo={`Por dia (últimos ${resumo.janelaDias} dias)`}
            colunas={COLUNAS_POR_DIA}
            itens={resumo.porDia}
            chave={(d) => d.dia}
            vazio="Nenhum consumo nos últimos 30 dias."
          />
        </>
      )}
    </>
  );
};

export default AbaRadarAssistente;
